import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'emoji-picker-element';

const EMOJIPEDIA_URL = 'https://emojipedia.org/';

/** Emojipedia-style category order (matches emojipedia.org groupings). */
const EMOJI_CATEGORY_ORDER = [
  'smileys-emotion',
  'people-body',
  'animals-nature',
  'food-drink',
  'activities',
  'travel-places',
  'objects',
  'symbols',
  'flags',
];

/**
 * Category icon field: text input + light-themed emoji dropdown (emoji-picker-element).
 */
export default function CategoryEmojiPicker({ value, onChange, inputId = 'cat-icon', disabled = false }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const rootRef = useRef(null);
  const pickerHostRef = useRef(null);

  const handleSelect = useCallback(
    (unicode) => {
      const emoji = String(unicode || '').trim();
      if (emoji) onChange?.(emoji);
      setPickerOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    if (!pickerOpen) return undefined;

    function onPointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setPickerOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') setPickerOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen || !pickerHostRef.current) return undefined;

    const host = pickerHostRef.current;
    host.replaceChildren();

    const picker = document.createElement('emoji-picker');
    picker.classList.add('emojipedia-emoji-picker', 'light');

    picker.customCategoryOrder = EMOJI_CATEGORY_ORDER;

    const onEmojiClick = (event) => {
      const unicode = event.detail?.unicode;
      if (unicode) handleSelect(unicode);
    };

    picker.addEventListener('emoji-click', onEmojiClick);
    host.appendChild(picker);

    return () => {
      picker.removeEventListener('emoji-click', onEmojiClick);
      host.replaceChildren();
    };
  }, [pickerOpen, handleSelect]);

  return (
    <div className="category-emoji-field" ref={rootRef}>
      <div className="category-emoji-field__preview" aria-hidden>
        {value ? <span className="category-emoji-field__glyph">{value}</span> : <span className="category-emoji-field__empty">?</span>}
      </div>
      <div className="category-emoji-field__controls flex-grow-1 position-relative">
        <label className="form-label" htmlFor={inputId}>
          Icon (emoji)
        </label>
        <div className="input-group">
          <input
            id={inputId}
            className="form-control category-icon-input"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="e.g. 🍕"
            autoComplete="off"
            inputMode="text"
            disabled={disabled}
          />
          <button
            type="button"
            className={`btn btn-outline-primary category-emoji-field__toggle ${pickerOpen ? 'active' : ''}`}
            onClick={() => setPickerOpen((open) => !open)}
            disabled={disabled}
            aria-expanded={pickerOpen}
            aria-haspopup="dialog"
            aria-controls="category-emoji-dropdown"
          >
            Pick emoji
          </button>
        </div>

        {pickerOpen ? (
          <div id="category-emoji-dropdown" className="category-emoji-dropdown" role="dialog" aria-label="Emoji picker">
            <div ref={pickerHostRef} className="category-emoji-dropdown__picker" />
            <div className="category-emoji-dropdown__footer">
              <a href={EMOJIPEDIA_URL} target="_blank" rel="noopener noreferrer" className="small">
                Browse on Emojipedia ↗
              </a>
            </div>
          </div>
        ) : null}

        <div className="form-text">
          Open the picker or paste an emoji.{' '}
          <a href={EMOJIPEDIA_URL} target="_blank" rel="noopener noreferrer">
            Emojipedia
          </a>{' '}
          for meanings.
        </div>
      </div>
    </div>
  );
}
